<template>
	<div>
		<div class="b-slider" :class="classes">
			<div class="b-slider__line">
				<div
					class="b-slider__lineFill"
					:style="{
						left: ((value[0] - min) / (max - min)) * 100 + '%',
						right: ((max - value[1]) / (max - min)) * 100 + '%',
					}"
				></div>
			</div>
			<span
				ref="sliderOption"
				:data-num="num"
				class="b-slider__option"
				:class="getNumClass(num)"
				v-for="num in scale"
				:key="num"
				@mousedown="(e) => handleNumMouseDown(e, num)"
			>
				{{ num }}
			</span>
		</div>
	</div>
</template>

<script>
function precisionToStep(precision) {
	return 1 / Math.pow(10, precision);
}

export default {
	props: {
		step: {
			type: Number,
		},
		min: {
			type: Number,
			required: true,
		},
		max: {
			type: Number,
			required: true,
		},
		value: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			threshold: 10,
			locationsMap: [],
			isMouseDown: false,
			isChangingMax: false,
			isChangingMin: false,
			resizeListener: null,
			mouseMoveListener: null,
			mouseUpListener: null,
		};
	},
	computed: {
		innerValue: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			},
		},
		classes() {
			return [];
		},
		precision() {
			const testNum = this.min + this.max;
			const testStr = "" + testNum;

			if (testStr.includes(".")) {
				return testStr.split(".")[1].length;
			}

			return 0;
		},
		_step() {
			return this.step ? this.step : precisionToStep(this.precision);
		},
		scale() {
			const scale = [this.min];
			let lastNum = this.min;

			while (lastNum < this.max) {
				const newNum = Math.min(lastNum + this._step, this.max);
				scale.push(newNum);
				lastNum = newNum;
			}
			return scale;
		},
	},
	methods: {
		handleNumMouseDown(e, num) {
			this.isMouseDown = true;

			this.isChangingMin = num === this.value[0];
			this.isChangingMax = num === this.value[1];

			if (this.isChangingMin || this.isChangingMax) {
				if (this.isLocationRecalculationRequired) {
					this.calculateLocation();
				}
			}
		},

		handleWindowMouseMove(e) {
			if (this.isMouseDown) {
				const mouseXPos = e.clientX;

				for (let { num, center } of this.locationsMap) {
					if (Math.abs(mouseXPos - center) < this.threshold) {
						const [rangeMin, rangeMax] = this.value;

						if (this.isChangingMin) {
							if (num > rangeMax) {
								this.isChangingMin = false;
								this.isChangingMax = true;
								this.$emit("input", [rangeMax, num]);
							} else {
								this.$emit("input", [num, rangeMax]);
							}
						} else if (this.isChangingMax) {
							if (num < rangeMin) {
								this.isChangingMin = true;
								this.isChangingMax = false;
								this.$emit("input", [num, rangeMin]);
							} else {
								this.$emit("input", [rangeMin, num]);
							}
						}
						break;
					}
				}
			}
		},

		handleWindowMouseUp() {
			this.isMouseDown = false;
			this.isChangingMin = false;
			this.isChangingMax = false;
		},

		getNumClass(num) {
			const [rangeMin, rangeMax] = this.value;

			return {
				"is-active":
					(num === rangeMin && this.isChangingMin) ||
					(num === rangeMax && this.isChangingMax),
				"is-selected": num === rangeMin || num === rangeMax,
				"is-between": num > rangeMin && num < rangeMax,
			};
		},

		calculateLocation() {
			const halfWidth = this.$refs.sliderOption[0].offsetWidth / 2;

			this.$refs.sliderOption.forEach((el) => {
				const num = el.dataset.num;
				const { left } = el.getBoundingClientRect?.();

				this.locationsMap.push({
					num: +num,
					center: left + halfWidth,
				});
			});

			this.isLocationRecalculationRequired = false;
		},
	},

	mounted() {
		this.calculateLocation();

		this.resizeListener = () => {
			this.isLocationRecalculationRequired = true;
		};

		this.mouseMoveListener = (e) => {
			this.handleWindowMouseMove(e);
		};

		this.mouseUpListener = (e) => {
			this.handleWindowMouseUp(e);
		};

		window.addEventListener("resize", this.resizeListener);
		window.addEventListener("mousemove", this.mouseMoveListener);
		window.addEventListener("mouseup", this.mouseUpListener);
	},

	destroyed() {
		window.removeEventListener("resize", this.resizeListener);
		window.removeEventListener("mousemove", this.mouseMoveListener);
		window.removeEventListener("mouseup", this.mouseUpListener);
	},
};
</script>

<style lang="scss">
.b-slider {
	--size-option: 24px;
	--size-ratio-option-unselected: 0.3333;
	position: relative;
	display: inline-flex;
	justify-content: space-between;
	-webkit-user-select: none;
	-ms-user-select: none;
	user-select: none;
	isolation: isolate;
	min-width: 100px;
	width: 100%;
	padding: 4px 0;
}

.b-slider__line {
	position: absolute;
	z-index: -1;
	height: 1px;
	background: var(--color-neutral-200);
	left: calc(var(--size-option) / 2);
	right: calc(var(--size-option) / 2);
	top: 50%;
	transform: translateY(-50%);
}

.b-slider__lineFill {
	position: absolute;
	top: 0;
	bottom: 0;
	background-color: var(--color-primary-600);
}

.b-slider__option {
	width: var(--size-option);
	height: var(--size-option);
	border-radius: 50%;
	color: var(--color-primary-600);
	border: 1px solid var(--color-primary-200);
	background-color: var(--color-neutral-100); // colors/neutral/n100;
	font-size: var(--font-size-3xs);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: 0.12s;

	&:not(.is-selected) {
		transform: scale(var(--size-ratio-option-unselected));
		color: transparent;
		border-width: 2px;
	}

	&.is-selected {
		border-color: var(--color-primary-600);

		&:hover {
			background-color: var(--color-primary-200);
			border-color: var(--color-primary-700);
			color: var(--color-primary-800);
		}

		&:is(.is-active, :active) {
			background-color: var(--color-primary-800);
			border-color: var(--color-primary-800);
			color: var(--color-neutral-0);
		}
	}

	&.is-between {
		border-color: var(--color-primary-600);
	}
}
</style>
